/**
 * Team (formerly "Operators"; Customer > Configure > Team). The people who can log in.
 *
 * This screen replaces "open the identity provider's admin console and hand-type a numeric
 * site id onto a user attribute". Its audience is an MSP admin or a customer's property
 * manager, so it deliberately speaks in operators / role / sites and never surfaces
 * identity-provider vocabulary — realms, protocol mappers, claims, attributes.
 *
 * Three rules shape the design:
 *
 *  1. SITE ASSIGNMENT ONLY APPLIES BELOW org_admin. org_admin and msp_admin span every
 *     site in their customer by design, and the server ignores site_ids for them. The
 *     site picker therefore SAYS SO rather than silently dropping the value — a picker
 *     that accepts input it will discard is how people end up believing someone is confined
 *     when they are not.
 *  2. YOU CANNOT GRANT WHAT YOU DO NOT HOLD. The server refuses a role above the caller's
 *     own or a site outside their reach. The UI never offers those options, so the
 *     refusal is a shape the form cannot express rather than a 403 after typing.
 *  3. PREFLIGHT CAN SAY NO. If the identity provider is unreachable, or does not declare the
 *     attributes that carry site assignment, a created operator would look correct and
 *     be confined to nothing. That blocks creation inline, with the reason — not a toast
 *     that disappears.
 *  4. A TEMPORARY PASSWORD IS SHOWN EXACTLY ONCE. Email invites must come from the GuestTek
 *     email domain, and that sender is not configured yet. So a new person gets in with a
 *     temporary password: generated here (crypto.getRandomValues, to the realm policy), passed
 *     on out-of-band, and replaced by them at first sign-in, when they also set up an
 *     authenticator app. The server never echoes it, so the handover panel after a successful
 *     save is the only place it ever appears. The invite option stays visible but disabled,
 *     with the reason, and lights up from the server flag alone.
 *  5. ASK FOR WHAT THE SIGN-IN SERVICE WILL ASK FOR (2026-10-07). It requires an email, first
 *     name and last name on every account. Without them the person's first sign-in stopped
 *     on its own "Update account information" page, right after a temporary-password
 *     handover. The dialog asks for whatever preflight reports in `required_fields`, and the
 *     username is suggested from the email until the admin types their own.
 *  6. TAKING ACCESS AWAY TAKES EFFECT NOW. Removing someone, turning off their sign-in,
 *     lowering their role or removing a customer or site signs them out of every open
 *     session first (server-side). If that sign-out was incomplete, the server says so and
 *     the dialog shows it rather than closing as if all was well.
 *  7. OWNER DECISIONS OF 2026-10-08 (MR !221, docs/design/msp-platform-admin-split.md 1.3).
 *     MSP admin is offered only to a platform admin, and only a higher role sees the
 *     Edit / Reset / Remove buttons on a person (MSP admins do not manage each other). The
 *     Default pool is never offered as a customer. An edit starts from the person's real
 *     customer list and carries over every customer the editor does not manage: it used to
 *     send only the current customer, which dropped the others.
 *  8. PLATFORM STAFF AND TEST ACCOUNTS (owner decision 2026-10-15, after OP#1859 / !265). The
 *     list above shows the customer selected in the header, so GTK staff with NO customer
 *     appear on no customer's page. A platform admin gets a "Platform staff" section under
 *     it (server: GET /operators/platform-staff, platform_admin only). Test accounts are
 *     never listed on a customer's page; they appear under Platform staff with a "Test"
 *     badge. A platform admin flags or unflags one from either list. The flag changes no
 *     role, customer or site (lib/platformStaff).
 *  9. LAST SIGN-IN (OP#1897, 2026-10-18). Every row, on the Team list and under Platform
 *     staff, shows when the person last signed in (relative, exact time on hover), "Never (in
 *     the last 90 days)" when the sign-in history holds none, or "unknown" when the sign-in
 *     service could not be read. Two flags call out access to review: "Inactive 90+ days" and
 *     "Never signed in" (account over 30 days old). The server decides both from its cached
 *     copy of the login history; the page never asks the sign-in service per row
 *     (lib/signIn). No IP address is shown to anyone.
 */
import { useId, useMemo, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  useOperators,
  useOperatorPreflight,
  useCreateOperator,
  useUpdateOperator,
  useResetOperatorPassword,
  useResendOperatorInvite,
  useDeleteOperator,
  usePlatformStaff,
  useSetTestAccount,
  type OperatorBody,
} from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import { useSession } from "@/stores/session";
import { ROLE_ORDER, type Operator, type Role } from "@/types/domain";
import {
  canSubmitCreate,
  checkTempPassword,
  chunkForReading,
  generateTempPassword,
  inviteOption,
  type PasswordPolicy,
  type SignInMethod,
} from "@/lib/tempPassword";
import {
  FIELD_LABEL,
  PROFILE_FIELDS,
  buildOrgGrant,
  buildSiteGrant,
  canManageMember,
  grantableRoles as grantableRolesFor,
  initialOrgSelection,
  pickableOrgIds,
  profileErrors,
  requiredFields,
  suggestUsername,
  type ProfileField,
} from "@/lib/teamMember";
import {
  PLATFORM_STAFF_CREATE_ROLES,
  signInToggleLabel,
  sortStaff,
  staffScopeLabel,
  testToggleBody,
  testToggleLabel,
} from "@/lib/platformStaff";
import { signInView } from "@/lib/signIn";
import { fmtAbsolute } from "@/lib/format";

/** Plain-language role names. The wire values never reach the screen. */
const ROLE_LABEL: Record<Role, string> = {
  read_only: "View only",
  site_operator: "Site manager",
  org_admin: "Customer admin",
  msp_admin: "MSP admin",
  platform_admin: "Platform admin",
};

const ROLE_HINT: Record<Role, string> = {
  read_only: "Can see their sites. Changes nothing.",
  site_operator: "Runs their own sites — guest passwords, who is online.",
  org_admin: "Every site for this customer.",
  // Org-scoped since 2026-10-08: an MSP admin runs the customers it is given, not all of them.
  msp_admin: "Every customer they are given. Can add customers and their people.",
  platform_admin: "Every customer and the platform itself. Keep to one or two people.",
};

/** Roles below org_admin are confined to specific sites; the rest span the customer
 *  (platform_admin spans every customer). */
const SITE_SCOPED: ReadonlySet<Role> = new Set<Role>(["read_only", "site_operator"]);

function rank(role: Role): number {
  return ROLE_ORDER.indexOf(role);
}

/** The operator's effective role — the highest they hold, matching the backend's _max_rank. */
function effectiveRole(op: Operator): Role | null {
  // op.roles can be absent on a sparse operator payload; default to [] so a single
  // malformed row never crashes the whole screen (was "Cannot read ... 'filter'").
  const held = (op.roles ?? []).filter((r) => ROLE_ORDER.includes(r));
  if (held.length === 0) return null;
  return held.reduce((a, b) => (rank(a) >= rank(b) ? a : b));
}

export default function Operators() {
  const { data, isLoading, error } = useOperators();
  const preflight = useOperatorPreflight();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Operator | null>(null);
  const [resetting, setResetting] = useState<Operator | null>(null);
  const [removing, setRemoving] = useState<Operator | null>(null);

  const operators = data?.items ?? [];
  const blocked = preflight.data && !preflight.data.ready;
  // Rule 8: Platform staff and the test flag are platform-admin only (the server returns 403
  // to everyone else; this only avoids rendering a section that could never load).
  const isPlatformAdmin = !!useSession((s) => s.me)?.is_platform_admin;

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <PageHeader
          title="Team"
          subtitle="Who can sign in, what they can change, and which sites they see"
        />
        <RoleGate min="org_admin" mode="hide">
          <Button variant="primary" onClick={() => setCreating(true)} disabled={!!blocked}>
            Add team member
          </Button>
        </RoleGate>
      </div>

      {error && <ErrorBanner error={error} />}

      {/* Preflight failure is a CONFIGURATION state, not an operational one, so it uses the
          warn ramp and sits inline above the list. A toast would vanish before anyone had
          worked out what it meant. */}
      {blocked && (
        <div
          role="status"
          className="md-card mb-4 border-warn/40 bg-warn-soft p-4 text-sm text-warn"
        >
          <div className="font-medium">Team members cannot be added yet</div>
          <p className="mt-1 text-warn/90">
            {preflight.data?.configured
              ? "Meridian can reach the sign-in service, but it is not storing which sites a team member is assigned to. Anyone added now would be able to sign in and see nothing."
              : "Meridian is not connected to a sign-in service, so it cannot create logins."}
          </p>
          <p className="mt-2 font-mono text-xs text-warn/80">{preflight.data?.detail}</p>
        </div>
      )}

      {isLoading && <div className="text-sm text-ink-muted">Loading team…</div>}

      {data && operators.length === 0 && (
        <EmptyState
          title="No team members yet"
          hint="Add the customer's admin first — they can see every site. Then add a manager per site, who sees only theirs."
        />
      )}

      {operators.length > 0 && (
        <ul className="flex flex-col gap-2">
          {operators.map((op) => (
            <OperatorRow
              key={op.id}
              operator={op}
              canInvite={inviteOption(preflight.data).available}
              onEdit={() => setEditing(op)}
              onReset={() => setResetting(op)}
              onRemove={() => setRemoving(op)}
              testFlag={isPlatformAdmin}
            />
          ))}
        </ul>
      )}

      {isPlatformAdmin && <PlatformStaffSection blocked={!!blocked} />}

      {creating && <OperatorDialog onClose={() => setCreating(false)} />}
      {editing && <OperatorDialog operator={editing} onClose={() => setEditing(null)} />}
      {resetting && (
        <PasswordDialog operator={resetting} onClose={() => setResetting(null)} />
      )}
      {removing && <RemoveDialog operator={removing} onClose={() => setRemoving(null)} />}
    </div>
  );
}

/* ------------------------------------------------------------------------------------- */

function OperatorRow({
  operator,
  onEdit,
  onReset,
  onRemove,
  canInvite,
  testFlag = false,
  signInToggle = false,
  scopeLabel,
}: {
  operator: Operator;
  onEdit: () => void;
  onReset: () => void;
  onRemove: () => void;
  /** Server says email invites can be sent (the GuestTek sender is configured). */
  canInvite: boolean;
  /** Rule 8: offer "Mark as test account" / "Not a test account" (platform admin only). */
  testFlag?: boolean;
  /** Rule 8: offer "Turn on/off sign-in" inline (Platform staff rows). */
  signInToggle?: boolean;
  /** Replaces the sites line (Platform staff rows show customers, not this customer's sites). */
  scopeLabel?: string;
}) {
  const resend = useResendOperatorInvite();
  const setTest = useSetTestAccount();
  const toggleSignIn = useUpdateOperator();
  const [sentTo, setSentTo] = useState<string | null>(null);
  // See ScopeBar: never select sitesForCurrentOrg() -- it returns a fresh array each render
  // and Zustand's Object.is check then re-renders forever (React #185).
  const me = useSession((s) => s.me);
  const currentOrgId = useSession((s) => s.currentOrgId);
  const sites = useMemo(
    // me.sites can be undefined (e.g. an msp_admin session with no sites array); guard
    // with ?? [] so the operators screen renders instead of throwing on '.filter'.
    () => (me?.sites ?? []).filter((s) => s.org_id === currentOrgId),
    [me, currentOrgId],
  );
  const role = effectiveRole(operator);
  const siteScoped = role ? SITE_SCOPED.has(role) : false;
  // Decision 2 (2026-10-08) and the server's strict-outrank rule: only someone with a HIGHER
  // role (or a platform admin) may change, reset or remove this person, so an MSP admin
  // never manages a peer MSP admin. The buttons are hidden rather than left to a 403.
  const myRole = useSession((s) => s.effectiveRole());
  const manageable = canManageMember(ROLE_ORDER, myRole, !!me?.is_platform_admin, role);

  const buildingLabel = useMemo(() => {
    if (!siteScoped) return "Every site";
    if (operator.site_ids.length === 0) return "No sites";
    const names = operator.site_ids
      .map((id) => sites.find((s) => String(s.id) === String(id))?.name ?? `Site ${id}`)
      .sort();
    return names.length <= 2 ? names.join(", ") : `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
  }, [siteScoped, operator.site_ids, sites]);

  // Rule 9: last sign-in + flag, rendered from the server's `sign_in` (absent = "unknown").
  const signIn = signInView(operator.sign_in, Date.now(), fmtAbsolute);

  /* "No sites" is the one genuinely alarming state: they can sign in and see nothing,
     which reads to them as a broken product. It earns the warn ramp; everything else here
     is descriptive and stays neutral. */
  const noReach = siteScoped && operator.site_ids.length === 0;

  return (
    <li className="md-card flex flex-wrap items-center gap-3 p-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate font-medium text-ink">{operator.username}</span>
          {!operator.enabled && (
            <span className="rounded border border-line px-1.5 py-0.5 text-[0.625rem] uppercase tracking-wide text-ink-subtle">
              disabled
            </span>
          )}
          {operator.is_test && (
            /* Rule 8: a test / service account. Display-only; it grants nothing. */
            <span
              title="Test account: hidden from customer Team pages"
              className="rounded border border-warn/40 bg-warn-soft px-1.5 py-0.5 text-[0.625rem] uppercase tracking-wide text-warn"
            >
              test
            </span>
          )}
          {signIn.flagLabel && (
            /* Rule 9: access to review. Warn ramp, like "No sites": nothing is broken yet,
               but an unused login is risk with no benefit. */
            <span
              data-testid="sign-in-flag"
              title={signIn.flagHint ?? undefined}
              className="rounded border border-warn/40 bg-warn-soft px-1.5 py-0.5 text-[0.625rem] uppercase tracking-wide text-warn"
            >
              {signIn.flagLabel}
            </span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
          <span>{role ? ROLE_LABEL[role] : "No role"}</span>
          <span aria-hidden className="text-ink-subtle">
            ·
          </span>
          <span className={!scopeLabel && noReach ? "text-warn" : undefined}>
            {scopeLabel ?? buildingLabel}
          </span>
          {operator.email && (
            <>
              <span aria-hidden className="text-ink-subtle">
                ·
              </span>
              <span className="truncate">{operator.email}</span>
            </>
          )}
          <span aria-hidden className="text-ink-subtle">
            ·
          </span>
          <span data-testid="last-sign-in" title={signIn.title}>
            Last sign-in: {signIn.text}
          </span>
        </div>
      </div>
      {!manageable && (
        <span className="text-xs text-ink-subtle">Only a higher role can change them</span>
      )}
      <RoleGate min="org_admin" mode="hide">
        {/* Not rendered at all (rather than disabled) when the caller cannot manage them. */}
        <div className={manageable ? "flex items-center gap-2" : "hidden"} aria-hidden={!manageable}>
          <Button variant="secondary" onClick={onEdit}>
            Edit
          </Button>
          {canInvite && operator.email && (
            /* Invite links expire, land in spam, or arrive while someone is on leave.
               Without a resend the only recovery is deleting the operator, which loses
               their role and sites. Hidden until the server can send invites. */
            <Button
              variant="ghost"
              disabled={resend.isPending}
              onClick={() =>
                resend.mutate(operator.id, { onSuccess: (r) => setSentTo(r.sent_to) })
              }
            >
              {resend.isPending ? "Sending…" : sentTo ? "Invite sent" : "Resend invite"}
            </Button>
          )}
          {/* Always available: the same temporary-password flow as creating someone. */}
          <Button variant="ghost" onClick={onReset}>
            Reset password
          </Button>
          {signInToggle && (
            <Button
              variant="ghost"
              disabled={toggleSignIn.isPending}
              onClick={() =>
                toggleSignIn.mutate({ id: operator.id, enabled: operator.enabled === false })
              }
            >
              {signInToggleLabel(operator)}
            </Button>
          )}
          {testFlag && (
            <Button
              variant="ghost"
              disabled={setTest.isPending}
              onClick={() => setTest.mutate({ id: operator.id, ...testToggleBody(operator) })}
            >
              {testToggleLabel(operator)}
            </Button>
          )}
          <Button variant="ghost" onClick={onRemove}>
            Remove
          </Button>
        </div>
      </RoleGate>
      {(setTest.error || toggleSignIn.error) && (
        <div className="w-full">
          <ErrorBanner error={setTest.error ?? toggleSignIn.error} />
        </div>
      )}
    </li>
  );
}

/* ------------------------------------------------------------------------------------- */

function OperatorDialog({
  operator,
  onClose,
  platformStaff = false,
}: {
  operator?: Operator;
  onClose: () => void;
  /** Rule 8: adding GTK platform staff (no customer). Offers platform_admin only. */
  platformStaff?: boolean;
}) {
  const editingExisting = !!operator;
  const staffCreate = platformStaff && !editingExisting;
  const myRole = useSession((s) => s.effectiveRole());
  const me = useSession((s) => s.me);
  const currentOrgId = useSession((s) => s.currentOrgId);
  const isPlatformAdmin = !!me?.is_platform_admin;

  /* CUSTOMERS (owner decisions 4 + 6, 2026-10-08). The dialog used to send
     org_ids=[currentOrgId], which silently dropped every OTHER customer of a multi-customer
     operator on any edit. Now it starts from the operator's real customer list, lets the
     editor add or remove only its own customers (never the Default pool), and carries every
     other customer over untouched (buildOrgGrant). The server applies the same carry-over. */
  const pickable = useMemo(() => pickableOrgIds((me?.orgs ?? []).map((o) => o.id)), [me]);
  const [selectedOrgs, setSelectedOrgs] = useState<number[]>(() =>
    initialOrgSelection({ operatorOrgIds: operator?.org_ids ?? null, currentOrgId, pickable }),
  );
  const toggleOrg = (id: number) =>
    setSelectedOrgs((prev) => (prev.includes(id) ? prev.filter((o) => o !== id) : [...prev, id]));
  const orgName = (id: number | string) =>
    (me?.orgs ?? []).find((o) => Number(o.id) === Number(id))?.name ?? `Customer ${id}`;

  const sites = useMemo(
    // me.sites can be undefined (e.g. an msp_admin session with no sites array); guard
    // with ?? [] so the operators screen renders instead of throwing on '.filter'.
    // Sites of every SELECTED customer (was: the current customer only).
    () => (me?.sites ?? []).filter((s) => selectedOrgs.includes(Number(s.org_id))),
    [me, selectedOrgs],
  );

  const create = useCreateOperator();
  const update = useUpdateOperator();
  const preflight = useOperatorPreflight();
  const invite = inviteOption(preflight.data);
  const policy = preflight.data?.password_policy;

  const [username, setUsername] = useState(operator?.username ?? "");
  const [email, setEmail] = useState(operator?.email ?? "");
  const [firstName, setFirstName] = useState(operator?.first_name ?? "");
  const [lastName, setLastName] = useState(operator?.last_name ?? "");
  /* Rule 5: the username follows the email until the admin types in it themselves. */
  const [usernameEdited, setUsernameEdited] = useState(editingExisting);
  /* Errors appear per field once it has been left (blurred), so nobody is scolded for a
     field they have not reached yet. */
  const [touched, setTouched] = useState<Partial<Record<ProfileField, boolean>>>({});
  /* A sign-out warning from a change that took access away: shown instead of closing. */
  const [notice, setNotice] = useState<string | null>(null);
  const [role, setRole] = useState<Role>(
    staffCreate ? "platform_admin" : (effectiveRole(operator ?? ({} as Operator)) ?? "read_only"),
  );
  const [siteIds, setSiteIds] = useState<number[]>(operator?.site_ids ?? []);
  /* Temporary password is the default (and, today, the only working) method. */
  const [method, setMethod] = useState<SignInMethod>("temp_password");
  const [password, setPassword] = useState(() => generateTempPassword(policy));
  const [temporary, setTemporary] = useState(true);
  /* Set once the server has accepted the login: the dialog then shows the one-time handover
     instead of the form. Lives only in this component's state; gone when it unmounts. */
  const [handover, setHandover] = useState<{ username: string; password: string } | null>(null);

  /* Rule 2 rendered as a shape: only roles strictly below the caller's own are offered (a
     platform admin may also offer its own role; decision D-03, 2026-10-09), so "cannot grant
     a role equal to or above your own" is never a server refusal the user has to decode.
     Decision 2 (2026-10-08): MSP admin and above are offered to a platform admin only. */
  const grantableRoles = useMemo(
    () =>
      staffCreate
        ? // Rule 8: every other role needs a customer; those are added on the customer's page.
          grantableRolesFor(ROLE_ORDER, myRole, isPlatformAdmin).filter((r) =>
            (PLATFORM_STAFF_CREATE_ROLES as readonly Role[]).includes(r),
          )
        : grantableRolesFor(ROLE_ORDER, myRole, isPlatformAdmin),
    [myRole, isPlatformAdmin, staffCreate],
  );

  const siteScoped = SITE_SCOPED.has(role);
  // What will actually be sent (decision 6): the editor's selection plus everything it
  // cannot control, carried over.
  const orgGrant = buildOrgGrant({
    role,
    operatorOrgIds: operator?.org_ids ?? null,
    pickable,
    selected: selectedOrgs,
  });
  const keptOrgs = orgGrant.filter((id) => !pickable.includes(id));
  const needsCustomer = role !== "platform_admin" && orgGrant.length === 0;
  // A picker with one fixed choice is noise; show it when there is a real choice to make.
  const showOrgPicker =
    role !== "platform_admin" && (pickable.length > 1 || (selectedOrgs.length === 0 && pickable.length > 0));
  const pending = create.isPending || update.isPending;
  const failure = create.error ?? update.error;
  const pwCheck = checkTempPassword(password, { username, email, policy });
  // Rule 5: required on CREATE, as the realm says. An existing operator from before this
  // rule may lack them, so on edit a blank field means "leave as is" (the server agrees)
  // and only the email format is checked.
  const required = editingExisting ? new Set<ProfileField>() : requiredFields(preflight.data);
  const profile = { first_name: firstName ?? "", last_name: lastName ?? "", email: email ?? "" };
  const errors = profileErrors(profile, required);
  // The still-empty required fields, for the one-line prompt beside the disabled button.
  const missingLabels = PROFILE_FIELDS.filter(
    (f) => required.has(f) && !profile[f].trim(),
  ).map((f) => FIELD_LABEL[f].toLowerCase());
  const unsupported = preflight.data?.unsupported_required_attributes ?? [];
  // Rule 3: until preflight has answered ready, creation stays blocked.
  const ready = preflight.data ? preflight.data.ready : false;
  const createAllowed = canSubmitCreate({
    ready,
    username,
    method,
    inviteAvailable: invite.available,
    email: email ?? "",
    passwordOk: pwCheck.ok,
    profileOk: Object.keys(errors).length === 0,
  });
  const showError = (f: ProfileField) => (touched[f] ? errors[f] : undefined);
  const onEmailChange = (value: string) => {
    setEmail(value);
    if (!usernameEdited) setUsername(suggestUsername(value));
  };

  const toggleSite = (id: number) =>
    setSiteIds((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) return;
    if (Object.keys(errors).length > 0) {
      // Reveal every message at once rather than leaving a disabled button to decode.
      setTouched({ first_name: true, last_name: true, email: true });
      return;
    }
    const base = {
      username: username.trim(),
      email: (email ?? "").trim() || null,
      first_name: (firstName ?? "").trim() || null,
      last_name: (lastName ?? "").trim() || null,
      role,
      // Decision 6: the full intended customer list, never just the current customer.
      org_ids: orgGrant,
      // A role that spans the customer carries no site list; sending one would imply a
      // confinement that does not exist. Sites of a customer just removed are dropped;
      // sites the editor cannot see are carried over (buildSiteGrant).
      site_ids: buildSiteGrant({ siteScoped, siteIds, knownSites: me?.sites ?? [], orgIds: orgGrant }),
    };
    if (needsCustomer) return;
    if (editingExisting) {
      update.mutate(
        { id: operator!.id, ...base },
        // Rule 6: an incomplete sign-out is worth a pause, not a silent close.
        { onSuccess: (r) => (r.warning ? setNotice(r.warning) : onClose()) },
      );
      return;
    }
    if (!createAllowed) return;
    const body: OperatorBody =
      method === "invite" ? { ...base, invite: true } : { ...base, password, temporary };
    create.mutate(body, {
      onSuccess: (op) => {
        if (method === "invite") onClose();
        // The server never echoes the password: show the value generated here, once.
        else setHandover({ username: op.username, password });
      },
    });
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content
          /* While the one-time password is on screen, a stray click outside or Escape must
             not destroy it -- only the explicit "Done" closes the handover. */
          onInteractOutside={(e) => {
            if (handover) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (handover) e.preventDefault();
          }}
          className="md-dialog fixed left-1/2 top-1/2 z-sheet max-h-[90vh] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none"
        >
          {handover ? (
            <PasswordHandover
              title={`${handover.username} can now sign in`}
              username={handover.username}
              password={handover.password}
              temporary={temporary}
              onDone={onClose}
            />
          ) : (
            <>
              <Dialog.Title className="text-lg font-semibold text-ink">
                {editingExisting
                  ? `Edit ${operator!.username}`
                  : staffCreate
                    ? "Add platform staff"
                    : "Add team member"}
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-ink-muted">
                {editingExisting
                  ? "Change what they can do and which sites they see."
                  : staffCreate
                    ? "Create a sign-in for GTK staff who work across every customer."
                    : "Create a sign-in for someone at this customer."}
              </Dialog.Description>

              <form onSubmit={submit} className="mt-4 flex flex-col gap-4">
                {/* Rule 5. Order follows what the admin knows first: the person's name, then
                    their email, then the username suggested from it. */}
                <div className="grid gap-3 sm:grid-cols-2">
                  <TextField
                    label="First name"
                    value={firstName ?? ""}
                    onChange={setFirstName}
                    onBlur={() => setTouched((t) => ({ ...t, first_name: true }))}
                    required={required.has("first_name")}
                    optionalHint={!editingExisting}
                    error={showError("first_name")}
                    placeholder="Jo"
                    autoComplete="off"
                  />
                  <TextField
                    label="Last name"
                    value={lastName ?? ""}
                    onChange={setLastName}
                    onBlur={() => setTouched((t) => ({ ...t, last_name: true }))}
                    required={required.has("last_name")}
                    optionalHint={!editingExisting}
                    error={showError("last_name")}
                    placeholder="Okafor"
                    autoComplete="off"
                  />
                  <TextField
                    className="sm:col-span-2"
                    label="Email"
                    type="email"
                    value={email ?? ""}
                    onChange={onEmailChange}
                    onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                    required={required.has("email")}
                    optionalHint={!editingExisting}
                    error={showError("email") ?? (editingExisting ? errors.email : undefined)}
                    placeholder="j.okafor@example.com"
                    autoComplete="off"
                    hint={editingExisting ? undefined : "They use it to reset a forgotten password."}
                  />
                  <TextField
                    className="sm:col-span-2"
                    label="Username"
                    value={username}
                    onChange={(v) => {
                      setUsername(v);
                      setUsernameEdited(true);
                    }}
                    required
                    disabled={editingExisting}
                    placeholder="j.okafor"
                    autoComplete="off"
                    hint={
                      editingExisting
                        ? "Usernames cannot be changed."
                        : usernameEdited
                          ? "What they type to sign in."
                          : "Suggested from their email. You can change it."
                    }
                  />
                </div>
                {!editingExisting && unsupported.length > 0 && (
                  <p className="text-xs text-ink-muted">
                    At first sign-in they will also be asked for: {unsupported.join(", ")}.
                  </p>
                )}

                <fieldset className="flex flex-col gap-2">
                  <legend className="mb-1 text-sm text-ink-muted">What they can do</legend>
                  <div className="flex flex-col gap-1.5">
                    {grantableRoles.map((r) => (
                      <label
                        key={r}
                        className="flex cursor-pointer items-start gap-2.5 rounded border border-line bg-surface p-2.5 hover:bg-surface-2"
                      >
                        <input
                          type="radio"
                          name="role"
                          checked={role === r}
                          onChange={() => setRole(r)}
                          className="mt-0.5 accent-brand"
                        />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-ink">{ROLE_LABEL[r]}</span>
                          <span className="block text-xs text-ink-muted">{ROLE_HINT[r]}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                {/* Decisions 4 + 6 (2026-10-08): which customers. Only the editor's own
                    customers are offered, never the Default pool; customers the editor does
                    not manage are kept as they are, and the note says so. */}
                {showOrgPicker && (
                  <fieldset className="flex flex-col gap-2">
                    <legend className="mb-1 text-sm text-ink-muted">Which customers</legend>
                    <div className="flex flex-col gap-1 rounded border border-line bg-surface p-2">
                      {pickable.map((id) => (
                        <label key={id} className="flex cursor-pointer items-center gap-2 p-1 text-sm">
                          <input
                            type="checkbox"
                            checked={selectedOrgs.includes(id)}
                            onChange={() => toggleOrg(id)}
                            className="accent-brand"
                          />
                          <span className="text-ink">{orgName(id)}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                )}
                {role !== "platform_admin" && keptOrgs.length > 0 && (
                  <p className="rounded border border-line bg-surface-2 p-2.5 text-xs text-ink-muted">
                    They also have access to {keptOrgs.length} other customer
                    {keptOrgs.length === 1 ? "" : "s"} you do not manage. Saving keeps that access
                    as it is.
                  </p>
                )}
                {needsCustomer && (
                  <p className="text-xs text-warn">Choose at least one customer.</p>
                )}

                <fieldset className="flex flex-col gap-2">
                  <legend className="mb-1 text-sm text-ink-muted">Which sites</legend>
                  {siteScoped ? (
                    <>
                      <div className="flex flex-col gap-1 rounded border border-line bg-surface p-2">
                        {sites.length === 0 && (
                          <p className="p-1 text-xs text-ink-muted">
                            No sites exist yet. Add one first, or this operator will sign in
                            and see nothing.
                          </p>
                        )}
                        {sites.map((s) => (
                          <label key={s.id} className="flex cursor-pointer items-center gap-2 p-1 text-sm">
                            <input
                              type="checkbox"
                              checked={siteIds.includes(Number(s.id))}
                              onChange={() => toggleSite(Number(s.id))}
                              className="accent-brand"
                            />
                            <span className="text-ink">
                              {/* With several customers chosen, say whose site it is. */}
                              {selectedOrgs.length > 1 ? `${orgName(s.org_id)} · ${s.name}` : s.name}
                            </span>
                          </label>
                        ))}
                      </div>
                      {siteIds.length === 0 && sites.length > 0 && (
                        <p className="text-xs text-warn">
                          With no site selected they can sign in but will see nothing.
                        </p>
                      )}
                    </>
                  ) : (
                    /* Rule 1: say it, do not silently ignore the field. */
                    <p className="rounded border border-line bg-surface-2 p-2.5 text-xs text-ink-muted">
                      {ROLE_LABEL[role]} covers{" "}
                      {role === "platform_admin"
                        ? "every customer"
                        : role === "msp_admin"
                          ? "every site of the customers they are given"
                          : "every site for this customer"},
                      so there is nothing to choose here.
                    </p>
                  )}
                </fieldset>

                {!editingExisting && (
                  <fieldset className="flex flex-col gap-2">
                    <legend className="mb-1 text-sm text-ink-muted">How they sign in</legend>
                    <div className="flex flex-col gap-1.5">
                      <label className="flex cursor-pointer items-start gap-2.5 rounded border border-line bg-surface p-2.5 hover:bg-surface-2">
                        <input
                          type="radio"
                          name="signin"
                          checked={method === "temp_password"}
                          onChange={() => setMethod("temp_password")}
                          className="mt-0.5 accent-brand"
                        />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-ink">
                            Set a temporary password
                          </span>
                          <span className="block text-xs text-ink-muted">
                            You pass it on in person or by phone. They choose their own the
                            first time they sign in.
                          </span>
                        </span>
                      </label>
                      {/* Rule 4: visible but disabled, with the reason, until the server says
                          invites can go out from the GuestTek domain. */}
                      <label
                        aria-disabled={!invite.available}
                        className={
                          "flex items-start gap-2.5 rounded border border-line p-2.5 " +
                          (invite.available
                            ? "cursor-pointer bg-surface hover:bg-surface-2"
                            : "cursor-not-allowed bg-surface-2")
                        }
                      >
                        <input
                          type="radio"
                          name="signin"
                          checked={method === "invite"}
                          disabled={!invite.available}
                          onChange={() => setMethod("invite")}
                          className="mt-0.5 accent-brand"
                        />
                        <span className="min-w-0">
                          <span
                            className={
                              "block text-sm font-medium " +
                              (invite.available ? "text-ink" : "text-ink-subtle")
                            }
                          >
                            Send email invite
                          </span>
                          <span className="block text-xs text-ink-muted">
                            {invite.available
                              ? "They get a link to choose their own password. You never see it."
                              : invite.reason}
                          </span>
                        </span>
                      </label>
                    </div>

                    {method === "temp_password" && (
                      <TempPasswordField
                        value={password}
                        onChange={setPassword}
                        username={username}
                        email={email}
                        policy={policy}
                        temporary={temporary}
                        onTemporaryChange={setTemporary}
                      />
                    )}
                  </fieldset>
                )}

                {editingExisting && (
                  /* Rule 6. Taking access away signs them out on save (server-side). Added
                     access reaches an open session at its next re-check, every 5 minutes. */
                  <p className="rounded border border-line bg-surface-2 p-2.5 text-xs text-ink-muted">
                    Taking access away signs them out straight away. Added access reaches them
                    within 5 minutes, or the next time they sign in.
                  </p>
                )}

                {failure && <ErrorBanner error={failure} />}
                {notice && <SignOutNotice text={notice} />}

                {!editingExisting && missingLabels.length > 0 && (
                  /* Says why the button is disabled, in one line, before anyone has to ask. */
                  <p className="text-right text-xs text-ink-muted">
                    Add their {joinLabels(missingLabels)} to continue.
                  </p>
                )}

                <div className="mt-1 flex justify-end gap-2">
                  <Button variant="ghost" onClick={onClose}>
                    {notice ? "Close" : "Cancel"}
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={
                      pending ||
                      !!notice ||
                      !username.trim() ||
                      needsCustomer ||
                      (editingExisting ? !!errors.email : !createAllowed)
                    }
                  >
                    {pending
                      ? "Saving…"
                      : editingExisting
                        ? "Save changes"
                        : method === "invite"
                          ? "Send invite"
                          : "Add team member"}
                  </Button>
                </div>
              </form>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/* ------------------------------------------------------------------------------------- */

/**
 * The temporary password input, shared by Add team member and Reset password.
 *
 * Starts on a generated value (crypto.getRandomValues, to the realm policy, no look-alike
 * characters). "Type my own" switches to a plain-text field with the live policy checklist.
 * It is plain text on purpose: the admin is about to read it out to someone, so masking it
 * only invites typos.
 */
function TempPasswordField({
  value,
  onChange,
  username,
  email,
  policy,
  temporary,
  onTemporaryChange,
}: {
  value: string;
  onChange: (v: string) => void;
  username: string;
  email: string | null;
  policy?: PasswordPolicy;
  temporary: boolean;
  onTemporaryChange: (v: boolean) => void;
}) {
  const [typing, setTyping] = useState(false);
  const check = checkTempPassword(value, { username, email, policy });

  return (
    <div className="flex flex-col gap-2 rounded border border-line bg-surface-2 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-medium text-ink-muted">
          {typing ? "Their temporary password" : "Generated password"}
        </span>
        <div className="flex flex-wrap gap-1">
          {!typing && (
            <Button
              variant="ghost"
              className="text-xs"
              onClick={() => onChange(generateTempPassword(policy))}
            >
              Generate another
            </Button>
          )}
          <Button
            variant="ghost"
            className="text-xs"
            onClick={() => {
              // Switching back always draws a fresh value, so a half-typed password never
              // lingers as the "generated" one.
              onChange(typing ? generateTempPassword(policy) : "");
              setTyping(!typing);
            }}
          >
            {typing ? "Generate one instead" : "Type my own"}
          </Button>
        </div>
      </div>
      {typing ? (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="new-password"
          spellCheck={false}
          autoCapitalize="off"
          aria-label="Temporary password"
          className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand"
        />
      ) : (
        /* Shown in full here too: the admin should see what they are creating. The grouped
           handover view comes after the save succeeds. */
        <div className="break-all rounded border border-line bg-surface px-3 py-2 font-mono text-sm text-ink">
          {value}
        </div>
      )}
      {typing && (
        <ul className="flex flex-col gap-0.5 text-xs">
          {check.checks.map((c) => (
            <li key={c.id} className={c.ok ? "text-healthy" : "text-ink-muted"}>
              <span aria-hidden>{c.ok ? "✓ " : "· "}</span>
              {c.label}
            </li>
          ))}
        </ul>
      )}
      <label className="flex items-start gap-2 text-xs text-ink-muted">
        <input
          type="checkbox"
          checked={temporary}
          onChange={(e) => onTemporaryChange(e.target.checked)}
          className="mt-0.5 accent-brand"
        />
        <span>
          Make them choose their own password when they first sign in{" "}
          <span className="text-ink-subtle">(recommended)</span>. They will also set up an
          authenticator app if they do not have one.
        </span>
      </label>
    </div>
  );
}

/* ------------------------------------------------------------------------------------- */

/** Copy text; on failure select `el` so the admin can press Ctrl/Cmd+C themselves. */
async function copyOrSelect(text: string, el: HTMLElement | null): Promise<"copied" | "selected"> {
  try {
    if (!navigator.clipboard?.writeText) throw new Error("no clipboard API");
    await navigator.clipboard.writeText(text);
    return "copied";
  } catch {
    // The clipboard API is unavailable over plain HTTP, in some embedded browsers, and when
    // permission is denied. Selecting the text leaves the admin one keystroke away.
    if (el) {
      const range = document.createRange();
      range.selectNodeContents(el);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
    return "selected";
  }
}

/** A Copy button bound to one value, with its own "Copied" / "Press Ctrl+C" feedback. */
function CopyButton({
  text,
  target,
  label,
}: {
  text: string;
  target: React.RefObject<HTMLElement>;
  label: string;
}) {
  const [state, setState] = useState<"idle" | "copied" | "selected">("idle");
  return (
    <Button
      variant="secondary"
      className="shrink-0 text-xs"
      aria-label={label}
      onClick={() => {
        void copyOrSelect(text, target.current).then(setState);
      }}
    >
      {state === "copied" ? "Copied" : state === "selected" ? "Press Ctrl+C" : "Copy"}
    </Button>
  );
}

/**
 * The one-time handover. The temporary password is shown here and nowhere else, ever: the
 * server never echoes it, and the value lives only in the dialog's state. It is grouped in
 * fours so it can be read aloud. The groups are CSS gaps, not characters, so Copy and a manual
 * selection both produce the raw value. The sign-in address is included because "where do
 * I go?" is the next question.
 */
function PasswordHandover({
  title,
  username,
  password,
  temporary,
  onDone,
}: {
  title: string;
  username: string;
  password: string;
  temporary: boolean;
  onDone: () => void;
}) {
  const pwRef = useRef<HTMLElement>(null);
  const urlRef = useRef<HTMLElement>(null);
  const signInUrl = window.location.origin;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Dialog.Title className="text-lg font-semibold text-ink">{title}</Dialog.Title>
        <Dialog.Description className="mt-1 text-sm text-ink-muted">
          Give them these details in person or by phone, not by email or chat.
        </Dialog.Description>
      </div>

      <dl className="flex flex-col gap-3 rounded-lg border border-line bg-surface p-4">
        <div className="flex flex-col gap-1">
          <dt className="text-xs text-ink-muted">Sign in at</dt>
          <dd className="flex items-center justify-between gap-2">
            <code ref={urlRef} className="min-w-0 break-all font-mono text-sm text-ink">
              {signInUrl}
            </code>
            <CopyButton text={signInUrl} target={urlRef} label="Copy sign-in address" />
          </dd>
        </div>
        <div className="flex flex-col gap-1">
          <dt className="text-xs text-ink-muted">Username</dt>
          <dd className="font-mono text-sm text-ink">{username}</dd>
        </div>
        <div className="flex flex-col gap-1">
          <dt className="text-xs text-ink-muted">Temporary password</dt>
          <dd className="flex items-center justify-between gap-2">
            {/* select-all: one tap selects the whole value for the manual-copy fallback. */}
            <code
              ref={pwRef}
              aria-label="Temporary password"
              className="flex min-w-0 select-all flex-wrap gap-x-2.5 gap-y-1 font-mono text-lg tracking-wide text-ink"
            >
              {chunkForReading(password).map((g, i) => (
                <span key={i}>{g}</span>
              ))}
            </code>
            <CopyButton text={password} target={pwRef} label="Copy temporary password" />
          </dd>
        </div>
      </dl>

      <p role="note" className="rounded border border-warn/40 bg-warn-soft p-2.5 text-xs text-warn">
        This won't be shown again.{" "}
        {temporary
          ? "The user must change it at first login."
          : "It stays their password until someone resets it."}
      </p>

      <div className="flex justify-end">
        <Button variant="primary" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------- */

/**
 * Reset password: the same flow as creating someone. Generate (the default) or type,
 * temporary by default, then the one-time handover. Containment is enforced by the server: it
 * refuses a reset on anyone at or above the caller's role, or on anyone who also belongs to
 * a customer the caller cannot see.
 */
function PasswordDialog({ operator, onClose }: { operator: Operator; onClose: () => void }) {
  const reset = useResetOperatorPassword();
  const preflight = useOperatorPreflight();
  const policy = preflight.data?.password_policy;
  const [password, setPassword] = useState(() => generateTempPassword(policy));
  /* Default ON: an admin is setting a password for someone else, so the safe outcome is that
     the other person replaces it with one only they know. */
  const [temporary, setTemporary] = useState(true);
  const [done, setDone] = useState(false);
  const check = checkTempPassword(password, {
    username: operator.username,
    email: operator.email,
    policy,
  });

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content
          onInteractOutside={(e) => {
            if (done) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (done) e.preventDefault();
          }}
          className="md-dialog fixed left-1/2 top-1/2 z-sheet max-h-[90vh] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none"
        >
          {done ? (
            <PasswordHandover
              title={`New password for ${operator.username}`}
              username={operator.username}
              password={password}
              temporary={temporary}
              onDone={onClose}
            />
          ) : (
            <>
              <Dialog.Title className="text-lg font-semibold text-ink">
                Reset the password for {operator.username}
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-ink-muted">
                Their current password stops working and they are signed out everywhere as soon
                as you save. You will see the new one once, to pass on to them yourself.
              </Dialog.Description>
              <form
                className="mt-4 flex flex-col gap-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!check.ok) return;
                  reset.mutate(
                    { id: operator.id, password, temporary },
                    { onSuccess: () => setDone(true) },
                  );
                }}
              >
                <TempPasswordField
                  value={password}
                  onChange={setPassword}
                  username={operator.username}
                  email={operator.email}
                  policy={policy}
                  temporary={temporary}
                  onTemporaryChange={setTemporary}
                />
                {reset.error && <ErrorBanner error={reset.error} />}
                <div className="mt-1 flex justify-end gap-2">
                  <Button variant="ghost" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary" disabled={reset.isPending || !check.ok}>
                    {reset.isPending ? "Resetting…" : `Reset password for ${operator.username}`}
                  </Button>
                </div>
              </form>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/* ------------------------------------------------------------------------------------- */

function RemoveDialog({ operator, onClose }: { operator: Operator; onClose: () => void }) {
  const remove = useDeleteOperator();
  const update = useUpdateOperator();
  /* Rule 6: both actions sign them out first. If that was incomplete, say so instead of
     closing as though they were already gone everywhere. */
  const [notice, setNotice] = useState<string | null>(null);
  const done = (r: { warning?: string | null }) => (r.warning ? setNotice(r.warning) : onClose());

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">
            Remove {operator.username}?
          </Dialog.Title>
          {/* Offer the reversible action beside the permanent one: "they left for now" and
              "they are gone" are different, and only one of them can be undone. */}
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            This deletes their sign-in permanently and signs them out everywhere. To stop them
            signing in without losing the account, turn it off instead.
          </Dialog.Description>
          {(remove.error || update.error) && (
            <div className="mt-3">
              <ErrorBanner error={remove.error ?? update.error} />
            </div>
          )}
          {notice && (
            <div className="mt-3">
              <SignOutNotice text={notice} />
            </div>
          )}
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            {notice ? (
              <Button variant="primary" onClick={onClose}>
                Close
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                {operator.enabled && (
                  <Button
                    variant="secondary"
                    disabled={update.isPending || remove.isPending}
                    onClick={() =>
                      update.mutate({ id: operator.id, enabled: false }, { onSuccess: done })
                    }
                  >
                    Turn off sign-in
                  </Button>
                )}
                <Button
                  variant="danger"
                  disabled={remove.isPending || update.isPending}
                  onClick={() => remove.mutate(operator.id, { onSuccess: done })}
                >
                  {remove.isPending ? "Removing…" : "Remove permanently"}
                </Button>
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/* ------------------------------------------------------------------------------------- */

/**
 * Rule 8: Platform staff, rendered for a platform admin only. GTK staff with no customer, then
 * the test accounts (badged). It reuses the Team dialogs and per-operator routes: a platform
 * admin may act on anyone, so nothing here needs a new write path except the test flag.
 */
function PlatformStaffSection({ blocked }: { blocked: boolean }) {
  const { data, isLoading, error } = usePlatformStaff(true);
  const preflight = useOperatorPreflight();
  const me = useSession((s) => s.me);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Operator | null>(null);
  const [resetting, setResetting] = useState<Operator | null>(null);
  const [removing, setRemoving] = useState<Operator | null>(null);
  const staff = useMemo(() => sortStaff(data?.items ?? []), [data]);
  const orgName = (id: number | string) =>
    (me?.orgs ?? []).find((o) => String(o.id) === String(id))?.name ?? `Customer ${id}`;

  return (
    <section aria-labelledby="platform-staff-heading" className="mt-8">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 id="platform-staff-heading" className="text-base font-semibold text-ink">
            Platform staff
          </h2>
          <p className="mt-0.5 text-sm text-ink-muted">
            GTK staff with no customer, and test accounts. Only platform admins see this list;
            none of these people appear on a customer&apos;s Team page.
          </p>
        </div>
        <Button variant="secondary" onClick={() => setCreating(true)} disabled={blocked}>
          Add platform staff
        </Button>
      </div>

      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="mt-3 text-sm text-ink-muted">Loading platform staff…</div>}
      {data && staff.length === 0 && (
        <EmptyState
          title="No platform staff"
          hint="Platform admins with no customer, and test accounts, are listed here."
        />
      )}
      {staff.length > 0 && (
        <ul className="mt-3 flex flex-col gap-2" data-testid="platform-staff-list">
          {staff.map((op) => (
            <OperatorRow
              key={op.id}
              operator={op}
              canInvite={inviteOption(preflight.data).available}
              onEdit={() => setEditing(op)}
              onReset={() => setResetting(op)}
              onRemove={() => setRemoving(op)}
              testFlag
              signInToggle
              scopeLabel={staffScopeLabel(op, orgName)}
            />
          ))}
        </ul>
      )}

      {creating && <OperatorDialog platformStaff onClose={() => setCreating(false)} />}
      {editing && <OperatorDialog operator={editing} onClose={() => setEditing(null)} />}
      {resetting && <PasswordDialog operator={resetting} onClose={() => setResetting(null)} />}
      {removing && <RemoveDialog operator={removing} onClose={() => setRemoving(null)} />}
    </section>
  );
}

/* ------------------------------------------------------------------------------------- */

/** "a", "a and b", "a, b and c" -- for one-line prompts. */
function joinLabels(labels: string[]): string {
  if (labels.length <= 1) return labels[0] ?? "";
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/**
 * A labelled text input with an optional hint and an inline error. The error REPLACES the
 * hint (one message per field at a time), is tied to the input with aria-describedby, and
 * turns the border critical. Required fields carry no asterisk: on create every field here is
 * required today, so the exception is labelled instead ("optional", when `optionalHint`).
 */
function TextField({
  label,
  value,
  onChange,
  onBlur,
  required = false,
  optionalHint = false,
  error,
  hint,
  className,
  type = "text",
  placeholder,
  autoComplete,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  required?: boolean;
  /** Label a not-required field "(optional)". Off on edit, where blank means "unchanged". */
  optionalHint?: boolean;
  error?: string;
  hint?: string;
  className?: string;
  type?: "text" | "email";
  placeholder?: string;
  autoComplete?: string;
  disabled?: boolean;
}) {
  const id = useId();
  const noteId = `${id}-note`;
  const note = error ?? hint;
  return (
    <div className={"flex flex-col gap-1 text-sm " + (className ?? "")}>
      <label htmlFor={id} className="text-ink-muted">
        {label}
        {!required && optionalHint && <span className="text-ink-subtle"> (optional)</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        aria-required={required}
        aria-invalid={!!error}
        aria-describedby={note ? noteId : undefined}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
        className={
          "rounded border bg-surface px-3 py-2 text-ink outline-none disabled:text-ink-subtle " +
          (error ? "border-critical focus:border-critical" : "border-line focus:border-brand")
        }
      />
      {note && (
        <span id={noteId} className={"text-xs " + (error ? "text-critical" : "text-ink-subtle")}>
          {note}
        </span>
      )}
    </div>
  );
}

/** The server signed someone out only partly (rule 6). Warn ramp: worth reading, not an error. */
function SignOutNotice({ text }: { text: string }) {
  return (
    <p role="status" className="rounded border border-warn/40 bg-warn-soft p-2.5 text-xs text-warn">
      Saved. {text}
    </p>
  );
}
